import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Brain,
  User,
  Briefcase,
  Bell,
  Heart,
  Plus,
  Trash2,
  Pencil,
  Check,
  Loader2,
  Sparkles,
  Bot
} from 'lucide-react';

const MEMORY_CATEGORIES = {
  general: { label: 'General facts', icon: Brain, color: 'text-blue-400' },
  preference: { label: 'Preferences', icon: Heart, color: 'text-pink-400' },
  contact: { label: 'Important contacts', icon: User, color: 'text-green-400' },
  project: { label: 'Projects & work', icon: Briefcase, color: 'text-amber-400' },
  reminder: { label: 'Reminders', icon: Bell, color: 'text-violet-400' }
};

// Source labels and icons
const SOURCE_CONFIG = {
  user: { label: 'Added by you', icon: User, color: 'text-muted-foreground' },
  assistant_suggestion: { label: 'Suggested by Arlo', icon: Bot, color: 'text-violet-400' },
  onboarding: { label: 'From onboarding', icon: Sparkles, color: 'text-blue-400' }
};

const MemoriesModal = ({
  showMemories,
  setShowMemories,
  memories,
  memoriesLoading,
  newMemory,
  setNewMemory,
  newMemoryCategory,
  setNewMemoryCategory,
  addMemory,
  deleteMemory,
  updateMemory
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('general');

  const startEditing = (memory) => {
    setEditingId(memory.id);
    setEditContent(memory.content);
    setEditCategory(memory.category);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
    setEditCategory('general');
  };

  const saveEdit = async (memoryId) => {
    if (!editContent.trim()) return;
    await updateMemory(memoryId, editContent.trim(), editCategory);
    cancelEditing();
  };

  return (
    <Dialog open={showMemories} onOpenChange={setShowMemories}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col" data-testid="memories-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-violet-400" />
            Arlo&apos;s Memories
          </DialogTitle>
          <DialogDescription>
            Teach Arlo facts about you that persist across all conversations
          </DialogDescription>
        </DialogHeader>

        {/* Add Memory Form */}
        <div className="border-b border-border pb-4 space-y-3">
          <div className="flex gap-2">
            <Input
              value={newMemory}
              onChange={(e) => setNewMemory(e.target.value)}
              placeholder="e.g., I prefer bullet points over paragraphs"
              onKeyDown={(e) => e.key === 'Enter' && addMemory()}
              className="flex-1"
              data-testid="new-memory-input"
            />
            <Button onClick={addMemory} disabled={!newMemory.trim()} size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(MEMORY_CATEGORIES).map(([key, { label, icon: Icon, color }]) => (
              <button
                key={key}
                onClick={() => setNewMemoryCategory(key)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs transition-colors ${
                  newMemoryCategory === key
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                }`}
                data-testid={`category-${key}`}
              >
                <Icon className={`h-3 w-3 ${newMemoryCategory === key ? '' : color}`} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Memories List */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {memoriesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : memories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Brain className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No memories yet</p>
              <p className="text-xs mt-1">
                Add facts about yourself that Arlo should always remember
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {memories.map((memory) => {
                const cat = MEMORY_CATEGORIES[memory.category] || MEMORY_CATEGORIES.general;
                const Icon = cat.icon;
                const isEditing = editingId === memory.id;

                return (
                  <div
                    key={memory.id}
                    className={`group rounded-lg transition-colors ${
                      isEditing
                        ? 'bg-accent/10 border border-accent/30 p-3'
                        : 'bg-muted/50 hover:bg-muted p-3'
                    }`}
                    data-testid={`memory-${memory.id}`}
                  >
                    {isEditing ? (
                      <div className="space-y-3">
                        <Input
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit(memory.id);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                          className="text-sm"
                          autoFocus
                          data-testid={`edit-memory-input-${memory.id}`}
                        />
                        <div className="flex gap-2 flex-wrap">
                          {Object.entries(MEMORY_CATEGORIES).map(([key, { label, icon: CatIcon, color }]) => (
                            <button
                              key={key}
                              onClick={() => setEditCategory(key)}
                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
                                editCategory === key
                                  ? 'bg-accent text-accent-foreground'
                                  : 'bg-background hover:bg-muted text-muted-foreground'
                              }`}
                            >
                              <CatIcon className={`h-3 w-3 ${editCategory === key ? '' : color}`} />
                              {label}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelEditing}
                            className="h-7 px-2 text-xs"
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => saveEdit(memory.id)}
                            disabled={!editContent.trim()}
                            className="h-7 px-3 text-xs gap-1"
                          >
                            <Check className="h-3 w-3" />
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${cat.color}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">{memory.content}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">{cat.label}</span>
                            {memory.source && SOURCE_CONFIG[memory.source] && (
                              <span className={`flex items-center gap-1 text-xs ${SOURCE_CONFIG[memory.source].color}`}>
                                •
                                {(() => {
                                  const SourceIcon = SOURCE_CONFIG[memory.source].icon;
                                  return <SourceIcon className="h-3 w-3" />;
                                })()}
                                {SOURCE_CONFIG[memory.source].label}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                          <button
                            onClick={() => startEditing(memory)}
                            className="p-1 hover:bg-background rounded"
                            title="Edit memory"
                            data-testid={`edit-memory-${memory.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                          </button>
                          <button
                            onClick={() => deleteMemory(memory.id)}
                            className="p-1 hover:bg-background rounded"
                            title="Delete memory"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="pt-3 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">{memories.length}/50 memories saved</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MemoriesModal;
